<template>
  <div class="d-flex align-items-center" v-if="isFetchingConfig">
    <spinner />
  </div>
  <div v-else>
    <h2 class="t-primary t-600 mb-4 t-xxlr">
        Export {{ getIndexAsString(scope) }}
    </h2>

    <template v-if="scope !== 'researchers' && scope !== 'news'">
      <p class="mb-0 ml-0">
        Choose your export template:
      </p>
      <p class="m-2 pt-1">
        <radio-button :name="`exportType`"
          :value="Template.NORMAL"
          :valueText="`Export <b>${ numOfDocs }</b> ${ getIndexAsString(scope) }
            <br /><span class='t-sm' style='color: rgba(var(--text-primary), 0.5)'>Standard export format</span>`"
          :isActive="selectedExport === Template.NORMAL"
          @input="handleRadioButton"
          class="mb-1"
        />

        <radio-button :name="`exportType`"
          :value="Template.CONTACTS"
          :valueText="`Export ${contactType} from <b>${ numOfDocs }</b> ${ getIndexAsString(scope) }
            <br /><span class='t-sm' style='color: rgba(var(--text-primary), 0.5)'>Optimized for retrieving contact information (LinkedIn, work email)</span>`"
          :isActive="selectedExport === Template.CONTACTS"
          @input="handleRadioButton"
        />
      </p>
    </template>
    <range-slider
      @slider:change-value="val => numOfDocs = val"
      :width="200"
      :min="0"
      :max="activeLimit"
      :step="rangeSliderStep"
      :value="defaultNumOfDocs()"
      class="mt-4 ml-0 mb-2"
    >
      <p class="mb-0">
        Number of search results to export:
        <template v-if="count > activeLimit"><span class='t-sm is-hover--blue' style='color: rgba(var(--text-primary), 0.5)' v-tooltip="`Export limits can be raised at an additional cost.<br/>To learn more, please book a call with our sales team.`">(up to {{ activeLimit }})</span></template>
      </p>
    </range-slider>
    <template v-if="configData.export_emails && (selectedExport === Template.CONTACTS || scope === 'researchers')">
      <callout icon="fa fa-envelope" class="bg-transparent-blue t-sm mt-4 pt-2 pb-2">
        <span class="t-primary">Email addresses are included where available. (We obtain them from corresponding author data in publications.)</span><br />
        <span class='t-sm' style='color: rgba(var(--text-primary), 0.75)'>These emails will be re-verified during export. This functionality is included with the email add-on and does not consume credits.</span>
      </callout>
      <checkbox
          class="py-2 px-3 rounded mb-2 mt-2 bg-highlight"
          :name="'toggle_emails'"
          :value="findEmails"
          :isDisabled="configData.contact_credits === 0"
          @toggle="findEmails = !findEmails"
        >
        <p class="m-0">
        <span class='t-sm t-primary'>Find additional emails? ({{ configData.contact_credits }} contact credits remaining)</span><br />
        <span class='t-sm' style='color: rgba(var(--text-primary), 0.75)'>We use 20+ data providers to actively find and verify emails for <b>all</b> exported contacts.<br />(consumes 1 contact credit per additional valid email found)</span>
        <template v-if="configData.contact_credits === 0"><br /><span class='t-sm t-primary'>Contact sales if you wish to purchase contact credits.</span></template>
        </p>
      </checkbox>
    </template>

    <block-preview-collapsible
        :title="'Overview of all columns'"
        :previewHeight="60"
        :openCollapsed="false"
        class="pb-2 pt-4"
        ref="columnOverview">
      <div class="row ml-3 t-sm mr-0 mt-2">
        <ul class="col col-12 col-md-4 mb-0" v-for="(fieldColumn, i) in fieldColumns" :key="`field-${i}`">
          <li v-for="field in fieldColumn" :key="field.name">
            {{ field.name }}
          </li>
        </ul>
      </div>
      <template v-if="!isEmpty(addons)">
        <p class="t-600 mb-0 mt-3 ml-2 t-sm">
          Add-on columns: <i class="fa fa-info-circle t-secondary ml-2" v-tooltip="'Add-on columns can be unlocked at an additional cost.<br /> To learn more, please book a call with our sales team.'" />
        </p>
        <div class="row m-3 t-sm">
          <ul class="col col-12 col-md-4 m-0" v-for="(addonColumn, i) in addonColumns" :key="`addon-${i}`">
            <li v-for="addon in addonColumn" :key="addon.name" class="position-relative">
              <i v-if="addon.locked && $can('export')" class="fa fa-lock"
                style="position: absolute; left: -16px; top: 4px;"
              />
              {{ addon.name }}
            </li>
          </ul>
        </div>
      </template>
    </block-preview-collapsible>
    <!-- <div class="t-sm">
      <div class="bg-transparent-blue bd-radius p-2 mt-4">
        <i class="t-secondary fa fa-info-circle mr-1" /><a href="https://www.academiclabs.com/contact-sales" target="_blank" class="t-600">Book a call</a>
        {{ infoCopy }}
      </div>
    </div> -->

    <static-messages
      v-if="error"
      :messages="error"
      :variant="`danger`"
      @closed="error = null"
    />

    <div class="d-flex flex-column-reverse flex-md-row justify-content-end mt-3">
      <button
        class="btn bg-transparent no-border t-primary ml-3 mb-2"
        type="button"
        @click="close">Close
      </button>

      <!-- Having this button didn't seem to have much effect apart from adding clutter; hiding it for now.
      <a
        class="ml-3 mb-2 btn btn-primary--green-dark"
        href="https://www.academiclabs.com/contact-sales"
        target="_blank"
      >
        <i class="fa fa-info-circle mr-1" /> Book a call
      </a> -->
      <template v-for="type in EXPORT_TYPES">
        <button
          :key="type.name"
          class="btn btn-primary px-4 ml-3 mb-2"
          type="button"
          @click="exportFile(type)"
          :disabled="isLoading || !$can('export') || numOfDocs === 0"
        >
          <i class="fa fa-arrow-down mr-1" />
          <span v-if="isLoading && loadingType === type.endpoint" class="dots">Working</span>
          <span v-else>{{ `Download ${type.name} file` }}</span>
          <i class="fa fa-lock semi-transparent ml-2" v-if="!$can('export')" />
        </button>
      </template>
    </div>
  </div>
</template>

<script>
import StaticMessages from '@/components/shared/StaticMessages'
import Spinner from '@/components/shared/Spinner'
import RadioButton from '@/components/shared/form/RadioButton'
import Checkbox from '@/components/shared/form/Checkbox2'
import RangeSlider from '@/components/shared/form/RangeSlider.vue'
import BlockPreviewCollapsible from '@/components/shared/BlockPreviewCollapsible'
import Callout from '@/components/shared/Callout'

import moment from 'moment'
import SearchService from '@/services/search'

// Enum for export templates
const Template = Object.freeze({
  NORMAL: 'normal',
  CONTACTS: 'contacts'
})

export default {
  name: 'exportModal',
  components: {
    StaticMessages,
    Spinner,
    RadioButton,
    Checkbox,
    RangeSlider,
    BlockPreviewCollapsible,
    Callout
  },
  data () {
    return {
      isFetchingConfig: true,
      configData: {},
      isLoading: false,
      loadingType: '',
      error: '',
      selectedExport: Template.NORMAL,
      findEmails: false,
      numOfDocs: 0,

      // Declare the "enums" here, so Vue can see them
      Template
    }
  },
  created () {
    this.EXPORT_TYPES = [
      { extension: 'csv', name: 'CSV', endpoint: 'csv' },
      { extension: 'xlsx', name: 'Excel', endpoint: 'excel' }
    ]
  },
  props: {
    scope: {
      type: String,
      required: true
    },
    state: {
      type: Object,
      required: true
    },
    count: {
      type: Number,
      required: true
    }
  },
  mounted () {
    const isWithSuggestions = !this.state.global && !this.$route.name.includes('bookmark_folders')
    SearchService.getExportConfig(this.scope, isWithSuggestions).then(response => {
      this.configData = response
      this.isFetchingConfig = false
      this.numOfDocs = this.defaultNumOfDocs()
    })
  },
  computed: {
    contactType () {
      // return 'leadership'
      switch (this.scope) {
        case 'projects':
          return 'participants'
        case 'publications':
          return 'authors'
        case 'patents':
          return 'inventors'
        case 'clinical_trials':
          return 'investigators'
        default:
          return 'leadership'
      }
    },
    itemLimit () {
      return this.count < this.configData.limits.item ? this.count : this.configData.limits.item
    },
    contactLimit () {
      return this.count < this.configData.limits.contact ? this.count : this.configData.limits.contact
    },
    activeLimit () {
      return this.isContactsExport ? this.contactLimit : this.itemLimit
    },
    infoCopy () {
      if (!this.configData.premium) return 'to enable export, or if you have any other questions.'
      return `to ${!this.configData.export_emails ? 'enable mail export, ' : ''} buy contact credits or raise export limits.`
    },
    isContactsExport () {
      return this.selectedExport === Template.CONTACTS
    },
    fields () {
      return this.configData[this.isContactsExport ? 'contact_columns' : 'columns']
        .filter(c => !c.add_on)
    },
    fieldColumns () {
      const columns = []
      const mid = Math.ceil(this.fields.length / 3)
      for (let col = 0; col < 3; col++) {
        columns.push(this.fields.slice(col * mid, col * mid + mid))
      }
      return columns
    },
    addons () {
      return this.configData[this.isContactsExport ? 'contact_columns' : 'columns']
        .filter(c => c.add_on)
    },
    addonColumns () {
      const columns = []
      const mid = Math.ceil(this.addons.length / 3)
      for (let col = 0; col < 3; col++) {
        columns.push(this.addons.slice(col * mid, col * mid + mid))
      }
      return columns
    },
    rangeSliderStep () {
      // Picks a step size that's just right .. not too finnicky, not too coarse
      if (this.activeLimit <= 30) {
        return 1
      } else if (this.activeLimit <= 300) {
        return 10
      } else {
        return 50
      }
    }
  },
  methods: {
    handleRadioButton (checked, value) {
      this.selectedExport = value
      this.$refs.columnOverview.calculateContentHeight()
    },
    close () {
      this.$events.$emit('modal:close')
    },
    defaultNumOfDocs () {
      return this.activeLimit < 1000 ? this.activeLimit : 1000
    },
    exportFile (exportType) {
      if (this.isLoading) return

      this.error = ''
      this.isLoading = true
      this.loadingType = exportType.endpoint

      var findEmails = this.findEmails
      if ((!this.isContactsExport && this.scope !== 'researchers') || !this.configData.export_emails) {
        // Only applicable for contact-centric and Researcher export.
        // You also must've purchased the email add-on.
        findEmails = false
      }

      SearchService.exportFile({
        ...this.state,
        index: this.scope,
        url: this.$route.fullPath,
        numberOfDocuments: this.numOfDocs,
        findEmails: findEmails
      }, exportType.endpoint, this.isContactsExport).then(response => {
        const humanReadableIndex = this.getIndexAsString(this.scope)
        if (findEmails) {
          // In this case, BE continues handling the export in the background. FE's job is done.
          this.close()
          return
        }

        this.isLoading = false
        this.loadingType = ''
        const fileName = `${humanReadableIndex}-${moment().format('YYYY-MM-DD')}.${exportType.extension}`
        // Generate blob from response and virtual link that will be 'clicked' to download the export-file
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', fileName)
        link.click()

        this.$flash(
          {
            message: `<b>${this.numOfDocs} ${humanReadableIndex}</b> were exported as a ${exportType.name} file to your Downloads folder.`
            // (Don't need this marketing page link anymore; it's no longer maintained anyway.)
            // cta: {
            //   icon: 'fa-info-circle',
            //   text: 'More info',
            //   action: () => {
            //     window.open('https://www.academiclabs.com/exportingdata', '_blank')
            //   }
            // }
          },
          'success')

        this.close()
      }).catch(() => {
        this.isLoading = false
        this.error = 'Something went wrong, please try again.'
      })
    }
  }
}
</script>
